import { useUpdateEffect } from 'ahooks';
import { uniqueId } from 'lodash-es';
import React from 'react';
import useStateRef from 'react-usestateref';
import { Box, ThemeUICSSObject } from '@apia/theme';
import { TRequireOnlyOne } from '../types';
import {
  autoDisconnectMutationObserver,
  getSpecificParent,
} from '../utils/dom';
import { addBoundary } from '../utils/number';
import { noNaN } from '../utils/util';
import EventEmitter from '../utils/util/eventEmitter';
import usePrevious from './usePrevious';
import useMousePosition from './useMousePosition';

export type TTooltip = {
  /**
   * Si se pasa esta propiedad, el tooltip se mostrará lo más cerca posible del
   * elemento sin taparlo. Si no se pasa, seguirá el puntero del mouse mientras
   * esté abierto.
   */
  attachToElement?: HTMLElement | null;
  closeOnTooltipClick?: boolean;
  closeOnClickOut?: boolean;
  /**
   * Si se pasa el parámetro **closeOnClickOut=true** se puede pasar una
   * función **closeOnClickOutChecker** que permite comprobar si se debe cerrar
   * o no el tooltip, dependiendo del elemento que se haya cliqueado. Esta
   * función será llamada en cada click y si devuelve false, el tooltip no será
   * cerrado.
   */
  closeOnClickOutChecker?: (element: HTMLElement) => boolean;
  closeOnMouseLeave?: boolean;
  closeOnEscape?: boolean;
  id: string;
  /**
   * Con esta propiedad se puede sobreescribir las propiedades de
   * posicionamiento por ejemplo, o cualquier otra propiedad de sx del tooltip.
   */
  sx?: ThemeUICSSObject;
  variant?: string;
} & TRequireOnlyOne<{
  /** Se mostrará el texto solamente si no se pasa children */
  text: string;
  /**
   * Se puede pasar un ReactNode como children para que lo muestre dentro del
   * tooltip
   */
  children: React.ReactNode;
}>;

const tooltipEmitter = new (class extends EventEmitter<{
  open: TTooltip;
  close: string;
}> {})();

let openTooltips: string[] = [];

/**
 * Devuelve dos métodos open y close, que permiten
 * controlar el estado del tooltip creado por el hook.
 *
 * Se debe pasar un objeto TTooltip con las propiedades:
 * 
 * **attachToElement** Permite pasar un elemento HTMLElement 
 * para que el tooltip se posicione lo más cerca posible de éste.
 * 
 * **closeOnClick:** Indica si el tooltip debe cerrarse 
 * automaticamente cuando se hace click sobre él.
 * 
 * **text:** El texto que va a mostrar el tooltip.
 *
 * **sx:** Un objeto sx de themeUI que será aplicado al
 * tooltip.
 *
 * **variant:** La variante por defecto que usan los
 * tooltips es layout.tooltips.default. Se recomienda
 * agregar los nuevos variantes dentro del documento
 * tooltips.ts que se encuentra dentro de styles y luego
 * utilizar las nuevas variantes de la forma
 * **layout.tooltips.newTooltipVariant**
 * 
 * @example
 * 
 * const { open } = useTooltip(
    React.useMemo(
      () => ({
        text: 'Prueba',
        closeOnClick: true,
        sx: {
          top: `${buttonRef?.getClientRects()[0].top ?? 0}px`,
          left: `${(buttonRef?.getClientRects()[0].left ?? 0) + 35}px`,
        },
      }),
      [buttonRef],
    ),
  );

  return <Button
   {...window.getVariant('inherit')}
    type="button"
    className="docTypes__info"
    onClick={React.useCallback(() => open(), [open])}
    ref={setButtonRef}
  >
    <FaInfoCircle />
  </Button>
 */
export default function useTooltip(tooltip: Omit<TTooltip, 'id'>) {
  const id = React.useMemo(() => uniqueId('tooltips'), []);
  const [, setIsOpen, isOpen] = useStateRef(false);

  React.useEffect(() => {
    function hideByScroll() {
      if (isOpen.current) {
        tooltipEmitter.emit('close', id);
      }
      setIsOpen(false);
    }

    window.addEventListener('scroll', hideByScroll);

    return () => window.removeEventListener('scroll', hideByScroll);
  }, [id, isOpen, setIsOpen]);

  React.useEffect(() => {
    const handleClose = (closeId: string) => {
      if (closeId === id) setIsOpen(false);
    };
    tooltipEmitter.on('close', handleClose);
    return () => {
      tooltipEmitter.off('close', handleClose);
    };
  }, [id, setIsOpen]);

  const open = React.useCallback(
    (newTooltip?: Omit<TTooltip, 'id'>) => {
      if (!isOpen.current || newTooltip !== undefined) {
        tooltipEmitter.emit('open', {
          ...(newTooltip ?? tooltip),
          id,
        } as TTooltip);
      }
      setIsOpen(true);
    },
    [id, isOpen, setIsOpen, tooltip],
  );

  useUpdateEffect(() => {
    if (isOpen.current) {
      open(tooltip);
    }
  }, [tooltip]);

  const prevAttach = usePrevious(tooltip.attachToElement);
  React.useEffect(() => {
    if (prevAttach.current && !tooltip.attachToElement) {
      if (isOpen.current) tooltipEmitter.emit('close', id);
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltip.attachToElement]);

  return {
    open: React.useCallback(() => {
      open();
    }, [open]),
    close: React.useCallback(() => {
      if (isOpen.current) tooltipEmitter.emit('close', id);
      setIsOpen(false);
    }, [id, isOpen, setIsOpen]),
    toggle: React.useCallback(() => {
      if (isOpen.current) {
        tooltipEmitter.emit('close', id);
        setIsOpen(false);
      } else open();
    }, [id, isOpen, open, setIsOpen]),
    get isOpen() {
      return isOpen.current;
    },
  };
}

const TooltipBox = React.memo((current: TTooltip) => {
  const mouseCoords = useMousePosition();
  const [ref, setRef] = React.useState<HTMLElement | null>(null);
  const [, setA] = React.useState(false);

  React.useLayoutEffect(() => {
    setTimeout(() => setA(true), 100);
  }, []);

  const [sx, setSx] = React.useState<ThemeUICSSObject>({
    ...current.sx,
    position: 'fixed',
    left: -1000,
    top: -1000,
    px: '10px',
  });

  React.useEffect(() => {
    if (current.attachToElement && ref) {
      const boundingRect = ref?.getBoundingClientRect();
      const width = noNaN(boundingRect?.width);
      const height = noNaN(boundingRect?.height);

      function calculatePosition() {
        if (current.attachToElement) {
          const elementRect = current.attachToElement.getBoundingClientRect();
          const left =
            elementRect.left + elementRect.width - 5 + width >
              window.innerWidth &&
            elementRect.left + elementRect.width > window.innerWidth / 2
              ? addBoundary(elementRect.left + 5 - width, 0)
              : elementRect.left + elementRect.width - 5;
          const top =
            elementRect.top + elementRect.height - 5 + height >
              window.innerHeight &&
            elementRect.top + elementRect.height > window.innerHeight / 2
              ? addBoundary(elementRect.top + 5 - height, 0)
              : elementRect.top;

          setSx((currentSx) => ({
            ...currentSx,
            top,
            left,
            position: current.sx?.position ?? 'fixed',
            zIndex: 'tooltip',
          }));
        }
      }

      calculatePosition();

      return autoDisconnectMutationObserver(
        current.attachToElement,
        calculatePosition,
      );
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.attachToElement, ref]);

  const close = React.useCallback(() => {
    tooltipEmitter.emit('close', current.id);
  }, [current.id]);

  React.useEffect(() => {
    const handleEscape = (ev: KeyboardEvent) => {
      if (ev.code === 'Escape') {
        if (current.closeOnEscape) close();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [close, current.closeOnEscape]);

  React.useEffect(() => {
    const boundingRect = ref?.getBoundingClientRect();
    const width = noNaN(boundingRect?.width);
    const height = noNaN(boundingRect?.height);
    if (!current.attachToElement) {
      let left = noNaN(current.sx?.left ?? mouseCoords.x) + 20;
      let top = noNaN(current.sx?.top ?? mouseCoords.y);

      if (left + width > window.innerWidth && left > width / 2) {
        left = addBoundary(left - width - 40, 0);
      }
      if (top + height > window.innerHeight && top > height / 2) {
        top = addBoundary(top - height, 0);
      }

      setSx((currentSx) => ({
        ...currentSx,
        top,
        left,
        position: current.sx?.position ?? 'fixed',
        zIndex: 'tooltip',
      }));
    }
  }, [current.attachToElement, current.sx, mouseCoords.x, mouseCoords.y, ref]);

  React.useLayoutEffect(() => {
    if (current.closeOnClickOut) {
      function closeOnMouseDown(ev: MouseEvent) {
        if (
          openTooltips.includes(current.id) &&
          !getSpecificParent(
            ev.target as HTMLElement,
            (search) => search === ref,
          ) &&
          (!current.closeOnClickOutChecker ||
            current.closeOnClickOutChecker(ev.target as HTMLElement))
        )
          close();
      }
      document.addEventListener('mousedown', closeOnMouseDown);

      return () => {
        document.removeEventListener('mousedown', closeOnMouseDown);
      };
    }
    return () => {};
  }, [close, current, current.closeOnClickOut, current.id, ref]);

  return (
    <Box className="tooltip" ref={setRef} id={`tooltip${current.id}`} sx={sx}>
      <Box
        variant={current.variant ?? 'layout.common.components.tooltips.default'}
        onMouseDown={React.useCallback(() => {
          if (current.closeOnTooltipClick) close();
        }, [close, current.closeOnTooltipClick])}
        onMouseLeave={React.useCallback(() => {
          if (current.closeOnMouseLeave) close();
        }, [close, current.closeOnMouseLeave])}
        role="alert"
      >
        {ref && (current.children ?? current.text)}
      </Box>
    </Box>
  );
});

/**
 * Este Provider **no está pensado para ser reutilzado**,
 * en cambio, se debería usar una única vez en toda la
 * aplicación.
 */
export const TooltipsProvider = () => {
  const [tooltips, setTooltips] = React.useState<
    Record<string, TTooltip | null>
  >({});

  React.useEffect(() => {
    tooltipEmitter.on('open', (newTooltip) => {
      setTooltips((current) => {
        const newTooltips = { ...current, [newTooltip.id]: newTooltip };
        openTooltips = Object.keys(newTooltips);
        return newTooltips;
      });
    });
    tooltipEmitter.on('close', (id) => {
      setTooltips((current) => {
        const newTooltips = { ...current };
        delete newTooltips[id];
        openTooltips = Object.keys(newTooltips);
        return newTooltips;
      });
    });
  }, []);

  return (
    <>
      {Object.values(tooltips).map((current) => {
        if (current === null) return null;
        return <TooltipBox key={current.id} {...current} />;
      })}
    </>
  );
};
